import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGroupingModals } from '../components/GroupingModals/GroupingModalsProvider';
import { toGroupOptions } from '../components/selectorOptions';
import { toId } from '../domain/groupIds';
import { useGrouping } from './useGrouping';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { GroupFamily, GroupView } from '../domain/grouping.types';

type SelectorChange = SelectorOptionPropsInterface | SelectorOptionPropsInterface[];

export interface GroupFilterConfig {
  label: string;
  onChange: (options: SelectorChange) => void;
  options: SelectorOptionPropsInterface[];
  value: SelectorOptionPropsInterface[];
}

export interface UseListGroupingResult {
  /** Back to "All" in the Groups filter. */
  clearGroupFilter: () => void;
  /** Deletes items of the list, deleting the groups they leave empty (asks first when ads use them). */
  deleteItems: (itemIds: string[]) => void;
  /** Opens "Edit Groups" on the detail of a group (group menu of a row). */
  editGroup: (groupId: string) => void;
  /** "Filter by: Groups" config for the list header (visible groups plus the screens "Ungrouped" one). */
  groupFilter: GroupFilterConfig;
  /** Visible groups an item belongs to (never the default one), in the order of its `groups` ids. */
  groupsOf: (groupIds: unknown[] | undefined) => GroupView[];
  /** Some group is filtering the list. */
  hasActiveGroupFilter: boolean;
  isHighlighted: (itemId: string) => boolean;
  matchesGroupFilter: (itemGroupIds: string[]) => boolean;
}

const ALL_VALUE = '';
/** Virtual "Ungrouped" option of families without a default group (never collides with a group id). */
const UNGROUPED_VALUE = '__ungrouped__';

/** Grouping concerns shared by the Locations, Screens and Venues sidebar lists. */
export function useListGrouping(family: GroupFamily): UseListGroupingResult {
  const { formatMessage } = useIntl();
  const { filterGroups, groups, hasUngroupedItems } = useGrouping(family);
  const { deleteItems, highlightedItemIds, openGroupModal } = useGroupingModals();
  const [pickedGroupIds, setPickedGroupIds] = useState<string[]>([]);

  const groupsById = useMemo(() => new Map(groups.map(group => [group.id, group])), [groups]);
  const hasVirtualUngrouped = family !== 'screens' && hasUngroupedItems;
  const filterGroupIds = useMemo(
    () => new Set([...filterGroups.map(group => group.id), ...(hasVirtualUngrouped ? [UNGROUPED_VALUE] : [])]),
    [filterGroups, hasVirtualUngrouped]
  );
  // Picked groups that were deleted or hidden meanwhile no longer filter anything
  const activeGroupIds = useMemo(
    () => pickedGroupIds.filter(id => filterGroupIds.has(id)),
    [pickedGroupIds, filterGroupIds]
  );

  const groupsOf = useCallback(
    (groupIds: unknown[] | undefined) =>
      [...new Set((groupIds ?? []).map(toId))].flatMap(id => groupsById.get(id) ?? []),
    [groupsById]
  );

  const isHighlighted = useCallback((itemId: string) => highlightedItemIds.includes(itemId), [highlightedItemIds]);

  const matchesGroupFilter = useCallback(
    (itemGroupIds: string[]) =>
      activeGroupIds.length === 0 ||
      activeGroupIds.some(id =>
        id === UNGROUPED_VALUE ? !itemGroupIds.some(groupId => groupsById.has(groupId)) : itemGroupIds.includes(id)
      ),
    [activeGroupIds, groupsById]
  );

  const allOption: SelectorOptionPropsInterface = {
    id: 'all',
    label: formatMessage({ defaultMessage: 'All', id: 'multiFlightListsMap.filters.group.all' }),
    value: ALL_VALUE
  };
  const ungroupedLabel = formatMessage({
    defaultMessage: 'Ungrouped',
    id: 'multiFlightListsMap.filters.group.ungrouped'
  });
  const virtualUngrouped: SelectorOptionPropsInterface[] = hasVirtualUngrouped
    ? [{ id: UNGROUPED_VALUE, label: ungroupedLabel, value: UNGROUPED_VALUE }]
    : [];
  const options = [
    allOption,
    ...virtualUngrouped,
    ...toGroupOptions(filterGroups).map((option, index) =>
      filterGroups[index].isDefault ? { ...option, label: ungroupedLabel } : option
    )
  ];

  const handleFilterChange = (selected: SelectorChange) => {
    const picked = Array.isArray(selected) ? selected : [selected];
    const groupIds = picked.filter(option => option.value !== ALL_VALUE).map(option => String(option.value));
    // Picking "All" while groups are picked clears them; picking a group while "All" is shown replaces it
    const pickedAll = activeGroupIds.length > 0 && picked.some(option => option.value === ALL_VALUE);
    setPickedGroupIds(pickedAll ? [] : groupIds);
  };

  return {
    clearGroupFilter: () => setPickedGroupIds([]),
    deleteItems,
    editGroup: groupId => openGroupModal({ groupId, mode: 'edit' }),
    groupFilter: {
      label: formatMessage({ defaultMessage: 'Groups', id: 'multiFlightListsMap.filters.groups.label' }),
      onChange: handleFilterChange,
      options,
      value: activeGroupIds.length
        ? options.filter(option => activeGroupIds.includes(String(option.value)))
        : [allOption]
    },
    groupsOf,
    hasActiveGroupFilter: activeGroupIds.length > 0,
    isHighlighted,
    matchesGroupFilter
  };
}
